(() => {
  'use strict';

  const labels = {
    en: 'Choose product variant',
    es: 'Elegir variante del producto',
    fr: 'Choisir une variante du produit',
  };

  let variantIdSequence = 0;
  const observedShadowRoots = new WeakSet();
  const essentialCartSubtotalSignatures = new WeakMap();
  const essentialCartRoots = new Set();
  let repairScheduled = false;
  let essentialCartSyncTimer = 0;
  let essentialCartSyncInFlight = false;
  let essentialCartSyncPending = false;
  let essentialCartNextRetryAt = 0;

  const normaliseText = (value) => (value || '').replace(/\s+/g, ' ').trim();

  const plugCopy = {
    en: {
      recommended: 'Recommended for',
      mismatch: 'This is a {selected} plug. For {country}, choose the {preferred} plug or confirm that you already have a compatible certified adapter.',
      unavailable: 'No {preferred} plug option is listed for {country}. Only order if you already have a compatible certified adapter.',
    },
    es: {
      recommended: 'Recomendado para',
      mismatch: 'Este es un enchufe {selected}. Para {country}, elige el enchufe {preferred} o confirma que ya tienes un adaptador certificado compatible.',
      unavailable: 'No aparece ninguna opción de enchufe {preferred} para {country}. Compra solo si ya tienes un adaptador certificado compatible.',
    },
    fr: {
      recommended: 'Recommandé pour',
      mismatch: 'Il s’agit d’une prise {selected}. Pour {country}, choisissez la prise {preferred} ou confirmez que vous disposez déjà d’un adaptateur certifié compatible.',
      unavailable: 'Aucune option de prise {preferred} n’est proposée pour {country}. Commandez uniquement si vous disposez déjà d’un adaptateur certifié compatible.',
    },
  };

  const plugNamePattern = /(?:^|\s|\/)(AU|EU|UK|US)(?:\s|$|plug)/i;

  const identifyPlug = (value) => {
    const match = normaliseText(value).match(plugNamePattern);
    return match ? match[1].toUpperCase() : '';
  };

  const interpolatePlugCopy = (template, values) => Object.entries(values).reduce(
    (message, [key, value]) => message.replaceAll(`{${key}}`, value),
    template
  );

  const getPlugMarketContext = () => {
    const context = document.querySelector('[data-nexara-product-conversion][data-nexara-preferred-plug]');
    if (context) {
      return {
        country: context.getAttribute('data-nexara-market-country-name') || context.getAttribute('data-nexara-market-country') || '',
        preferred: context.getAttribute('data-nexara-preferred-plug') || '',
      };
    }

    const countryCode = normaliseText(
      window.Shopify?.country
      || document.querySelector('input[name="country_code"]')?.value
      || document.documentElement.getAttribute('data-country-code')
    ).toUpperCase();
    const countryLabel = normaliseText(
      document.querySelector('button[aria-controls*="CountryList"]')?.textContent
      || document.querySelector('[data-localization-form] button')?.textContent
    ).split('|')[0].trim();
    const activeCurrency = normaliseText(window.Shopify?.currency?.active).toUpperCase();
    if (!countryCode && !countryLabel && !activeCurrency) return null;

    const preferredByCountry = {
      AU: 'AU', NZ: 'AU',
      GB: 'UK', IE: 'UK', CY: 'UK', AE: 'UK',
      US: 'US', CA: 'US',
    };
    const displayNames = typeof Intl.DisplayNames === 'function'
      ? new Intl.DisplayNames([document.documentElement.lang || 'en'], { type: 'region' })
      : null;
    const preferredFromLabel = /Australia|New Zealand/i.test(countryLabel)
      ? 'AU'
      : /United Kingdom|Ireland|Cyprus|United Arab Emirates/i.test(countryLabel)
        ? 'UK'
        : /United States|Canada/i.test(countryLabel)
          ? 'US'
          : 'EU';
    const preferredByCurrency = {
      AUD: 'AU', NZD: 'AU',
      GBP: 'UK', AED: 'UK',
      USD: 'US', CAD: 'US',
    };
    return {
      country: (countryCode && displayNames?.of(countryCode)) || countryLabel || countryCode || activeCurrency,
      preferred: (countryCode && preferredByCountry[countryCode])
        || (countryLabel && preferredFromLabel)
        || preferredByCurrency[activeCurrency]
        || 'EU',
    };
  };

  const getPlugFieldset = () => {
    const fieldsets = [...document.querySelectorAll('fieldset')];
    return fieldsets.find((fieldset) => {
      const legend = normaliseText(fieldset.querySelector('legend')?.textContent);
      const values = [...fieldset.querySelectorAll('input[type="radio"]')].map((input) => identifyPlug(input.value));
      return /plug|socket|adapter/i.test(legend) || values.filter(Boolean).length >= 2;
    }) || null;
  };

  const repairPlugCompatibility = () => {
    const context = getPlugMarketContext();
    const fieldset = getPlugFieldset();
    if (!context || !fieldset || !context.preferred) return;

    const variantRoot = fieldset.closest('variant-selects, variant-radios') || fieldset;
    const inputs = [...fieldset.querySelectorAll('input[type="radio"]')];
    const preferredInputs = inputs.filter((input) => identifyPlug(input.value) === context.preferred);
    const availablePreferred = preferredInputs.find((input) => !input.disabled && !input.classList.contains('disabled'));

    if (!variantRoot.hasAttribute('data-nexara-plug-default-applied')) {
      variantRoot.setAttribute('data-nexara-plug-default-applied', '');
      const current = inputs.find((input) => input.checked);
      if (availablePreferred && identifyPlug(current?.value) !== context.preferred) {
        availablePreferred.click();
        return;
      }
    }

    let warning = fieldset.querySelector('[data-nexara-plug-warning]');
    if (!warning) {
      warning = document.createElement('p');
      warning.className = 'nexara-plug-warning';
      warning.setAttribute('data-nexara-plug-warning', '');
      warning.setAttribute('role', 'status');
      fieldset.appendChild(warning);
    }

    const language = document.documentElement.lang.slice(0, 2);
    const copy = plugCopy[language] || plugCopy.en;
    const selected = identifyPlug(inputs.find((input) => input.checked)?.value);
    const hasPreferred = preferredInputs.length > 0;
    const isCompatible = selected === context.preferred;

    fieldset.querySelectorAll('[data-nexara-plug-recommended]').forEach((label) => {
      label.removeAttribute('data-nexara-plug-recommended');
      label.removeAttribute('title');
    });
    preferredInputs.forEach((input) => {
      const label = fieldset.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      if (!label) return;
      label.setAttribute('data-nexara-plug-recommended', '');
      label.setAttribute('title', `${copy.recommended} ${context.country}`);
    });

    if (isCompatible) {
      warning.hidden = true;
      warning.textContent = '';
      return;
    }

    const template = hasPreferred ? copy.mismatch : copy.unavailable;
    warning.textContent = interpolatePlugCopy(template, {
      selected: selected || normaliseText(inputs.find((input) => input.checked)?.value) || 'different',
      preferred: context.preferred,
      country: context.country,
    });
    warning.hidden = false;
  };

  const shippingCopy = {
    en: {
      below: 'below',
      freeFrom: 'Free from',
      processing: 'Processing time: 1–3 business days.',
      shipping: 'Shipping',
      freeShipping: 'Free shipping',
      total: 'Total',
    },
    es: {
      below: 'por debajo de',
      freeFrom: 'Gratis desde',
      processing: 'Tiempo de preparación: 1–3 días laborables.',
      shipping: 'Envío',
      freeShipping: 'Envío gratis',
      total: 'Total',
    },
    fr: {
      below: 'sous',
      freeFrom: 'Gratuit dès',
      processing: 'Délai de préparation : 1 à 3 jours ouvrés.',
      shipping: 'Livraison',
      freeShipping: 'Livraison gratuite',
      total: 'Total',
    },
  };

  const getCurrencyContext = () => {
    const marketContext = document.querySelector('[data-nexara-market-currency]');
    const marketCurrency = marketContext && marketContext.getAttribute('data-nexara-market-currency');
    const currency = window.Shopify && window.Shopify.currency;
    const shopifyCurrency = currency && currency.active ? currency.active : '';
    const activeCurrency = marketCurrency || shopifyCurrency || 'AUD';
    const parsedRate = Number(currency && currency.rate);
    const rateMatchesMarket = activeCurrency === 'AUD'
      || (shopifyCurrency === activeCurrency && Number.isFinite(parsedRate) && parsedRate > 0);
    const rate = activeCurrency === 'AUD' ? 1 : rateMatchesMarket ? parsedRate : null;

    if (!Number.isFinite(rate) || rate <= 0) {
      return { activeCurrency, ready: false };
    }

    return {
      activeCurrency,
      ready: true,
      rate,
      shippingFeeCents: Math.round(850 * rate),
      freeShippingThresholdCents: Math.round(7000 * rate),
    };
  };

  const formatCurrencyAmount = (amount, currency) => {
    const formatter = new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
    });

    return formatter.format(amount);
  };

  const formatConvertedAud = (baseAudCents) => {
    const {
      activeCurrency,
      rate,
      ready,
      shippingFeeCents,
      freeShippingThresholdCents,
    } = getCurrencyContext();
    if (!ready) return null;
    const convertedCents = baseAudCents === 850
      ? shippingFeeCents
      : baseAudCents === 7000
        ? freeShippingThresholdCents
        : Math.round(baseAudCents * rate);

    return formatCurrencyAmount(convertedCents / 100, activeCurrency);
  };

  const shippingContextPattern = /shipping|delivery|tracked|free shipping|env[ií]o|entrega|livraison|exp[ée]dition/i;
  const unconvertedFeePattern = /(?:[A-Z]{0,2}\p{Sc}\s*8(?:[.,]50)?(?:\s*[A-Z]{3})?|8[.,]50\s*\p{Sc}(?:\s*[A-Z]{3})?|8[.,]50\s*[A-Z]{3})/gu;
  const unconvertedThresholdPattern = /(?:[A-Z]{0,2}\p{Sc}\s*70(?:[.,]00)?(?:\s*[A-Z]{3})?|70(?:[.,]00)?\s*\p{Sc}(?:\s*[A-Z]{3})?|70(?:[.,]00)?\s*[A-Z]{3})/gu;

  const replaceUnconvertedShippingAmounts = (value, shippingFee, freeShippingThreshold) => {
    return (value || '')
      .replace(unconvertedThresholdPattern, freeShippingThreshold)
      .replace(unconvertedFeePattern, shippingFee);
  };

  const repairShippingTextNodes = (root, shippingFee, freeShippingThreshold) => {
    if (!root || !document.createTreeWalker) return;
    const showText = window.NodeFilter ? window.NodeFilter.SHOW_TEXT : 4;
    const walker = document.createTreeWalker(root, showText);
    let node = walker.nextNode();

    while (node) {
      const parent = node.parentElement;
      const tagName = parent && parent.tagName;
      if (!tagName || !/^(SCRIPT|STYLE|TEXTAREA|NOSCRIPT)$/.test(tagName)) {
        const contextRoot = parent && typeof parent.closest === 'function'
          ? parent.closest('li, p, section, article, cart-drawer, .cart__footer, .cart-drawer__footer')
          : null;
        const context = normaliseText((contextRoot && contextRoot.textContent) || (parent && parent.textContent) || node.nodeValue);
        if (shippingContextPattern.test(context)) {
          shippingContextPattern.lastIndex = 0;
          const corrected = replaceUnconvertedShippingAmounts(node.nodeValue, shippingFee, freeShippingThreshold);
          if (corrected !== node.nodeValue) node.nodeValue = corrected;
        }
      }
      node = walker.nextNode();
    }
  };

  const repairShippingCurrency = () => {
    const language = document.documentElement.lang.slice(0, 2);
    const copy = shippingCopy[language] || shippingCopy.en;
    const shippingFee = formatConvertedAud(850);
    const freeShippingThreshold = formatConvertedAud(7000);
    if (!shippingFee || !freeShippingThreshold) return;

    document.querySelectorAll('.announcement-bar__message span').forEach((element) => {
      const text = element.textContent || '';
      if (!/free shipping|env[ií]o gratis|livraison gratuite/i.test(text)) return;

      const corrected = text.replace(
        /((?:free shipping from|env[ií]o gratis desde|livraison gratuite d[èe]s)\s+).*?(?=\s*(?:•|·|$))/i,
        (match, prefix) => `${prefix}${freeShippingThreshold}`
      );

      if (corrected !== text) element.textContent = corrected;
    });

    document.querySelectorAll('.nexara-product-shipping__summary').forEach((element) => {
      const currentText = normaliseText(element.textContent);
      const arrival = currentText.match(/(?:Estimated arrival|Llegada estimada|Arrivée estimée)\s*:\s*.+$/i);
      const corrected = `${shippingFee} ${copy.below} ${freeShippingThreshold} · ${copy.freeFrom} ${freeShippingThreshold}${arrival ? ` · ${arrival[0]}` : ''}`;

      if (currentText !== corrected) element.textContent = corrected;
    });

    document.querySelectorAll('.nexara-product-shipping__note').forEach((element) => {
      if (normaliseText(element.textContent) !== copy.processing) {
        element.textContent = copy.processing;
      }
    });

    document.querySelectorAll('.nexara-product-shipping__icon').forEach((element) => {
      if (element.querySelector('svg')) return;
      element.innerHTML = '<svg viewBox="0 0 24 24" focusable="false" aria-hidden="true"><path d="M5.5 12.5 10 17l8.5-10" fill="none" stroke="currentColor" stroke-width="2.75" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
    });

    document.querySelectorAll(
      '.shopify-policy__body, .cart__footer, cart-drawer, .cart-drawer__footer, [data-essential-cart-element="cart-drawer-content"], [data-essential-cart-element="announcement-bar"]'
    ).forEach((root) => {
      repairShippingTextNodes(root, shippingFee, freeShippingThreshold);
    });

    scheduleEssentialCartTotals(document);
  };

  const setEssentialCartRow = (panel, subtotalRow, rowName, labelText, valueText) => {
    let row = panel.querySelector(`[data-nexara-essential-cart-row="${rowName}"]`);

    if (!row) {
      const sourceLabel = subtotalRow.querySelector('[data-essential-cart-element="cart-drawer-property-label"]');
      const sourceValue = subtotalRow.querySelector('[data-essential-cart-element="cart-drawer-property-value"]');
      row = document.createElement('div');
      row.className = subtotalRow.className;
      row.setAttribute('data-nexara-essential-cart-row', rowName);

      const label = document.createElement('span');
      label.className = sourceLabel ? sourceLabel.className : '';
      label.setAttribute('data-nexara-essential-cart-label', '');

      const value = document.createElement('span');
      value.className = sourceValue ? sourceValue.className : '';
      value.setAttribute('data-nexara-essential-cart-value', '');

      row.append(label, value);
      panel.appendChild(row);
    }

    const label = row.querySelector('[data-nexara-essential-cart-label]');
    const value = row.querySelector('[data-nexara-essential-cart-value]');
    if (label.textContent !== labelText) label.textContent = labelText;
    if (value.textContent !== valueText) value.textContent = valueText;
    row.setAttribute('aria-label', `${labelText}: ${valueText}`);
  };

  const renderEssentialCartTotals = (root, cart) => {
    const subtotalWrapper = root.querySelector('[data-essential-cart-element="subtotal-wrapper"]');
    const subtotalRow = subtotalWrapper && subtotalWrapper.querySelector(
      '[data-essential-cart-element="cart-drawer-property-row"]'
    );
    if (!subtotalWrapper || !subtotalRow) return;

    let panel = root.querySelector('[data-nexara-essential-cart-totals]');
    if (!panel) {
      panel = document.createElement('div');
      panel.setAttribute('data-nexara-essential-cart-totals', '');
      panel.setAttribute('aria-live', 'polite');
      subtotalWrapper.insertAdjacentElement('afterend', panel);
    }

    const language = document.documentElement.lang.slice(0, 2);
    const copy = shippingCopy[language] || shippingCopy.en;
    const {
      activeCurrency,
      ready,
      shippingFeeCents,
      freeShippingThresholdCents,
    } = getCurrencyContext();
    if (!ready) return;
    const cartCurrency = cart.currency || activeCurrency;
    const subtotalCents = Number.isFinite(Number(cart.total_price)) ? Number(cart.total_price) : 0;
    const hasFreeShipping = cart.requires_shipping === false || subtotalCents >= freeShippingThresholdCents;
    const appliedShippingCents = hasFreeShipping ? 0 : shippingFeeCents;
    const totalCents = subtotalCents + appliedShippingCents;
    const shippingValue = hasFreeShipping
      ? copy.freeShipping
      : formatCurrencyAmount(appliedShippingCents / 100, cartCurrency);
    const totalValue = formatCurrencyAmount(totalCents / 100, cartCurrency);

    setEssentialCartRow(panel, subtotalRow, 'shipping', copy.shipping, shippingValue);
    setEssentialCartRow(panel, subtotalRow, 'total', copy.total, totalValue);
    panel.setAttribute(
      'data-nexara-essential-cart-state',
      hasFreeShipping ? 'free-shipping' : 'paid-shipping'
    );
  };

  const syncEssentialCartTotals = async () => {
    if (essentialCartSyncInFlight) {
      essentialCartSyncPending = true;
      return;
    }

    essentialCartSyncInFlight = true;
    essentialCartSyncPending = false;

    try {
      const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
      const response = await window.fetch(`${rootUrl}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Cart totals unavailable');
      const cart = await response.json();

      essentialCartNextRetryAt = 0;
      essentialCartRoots.forEach((root) => {
        const isConnected = root === document || (root.host && root.host.isConnected);
        if (!isConnected) {
          essentialCartRoots.delete(root);
          return;
        }
        renderEssentialCartTotals(root, cart);
      });
    } catch (error) {
      essentialCartNextRetryAt = Date.now() + 5000;
    } finally {
      essentialCartSyncInFlight = false;
      if (essentialCartSyncPending) {
        essentialCartSyncPending = false;
        window.setTimeout(syncEssentialCartTotals, 100);
      }
    }
  };

  function scheduleEssentialCartTotals(root) {
    if (!root || typeof root.querySelector !== 'function') return;
    const subtotalWrapper = root.querySelector('[data-essential-cart-element="subtotal-wrapper"]');
    if (!subtotalWrapper) return;

    const subtotalSignature = normaliseText(subtotalWrapper.textContent);
    const panelExists = Boolean(root.querySelector('[data-nexara-essential-cart-totals]'));
    if (panelExists && essentialCartSubtotalSignatures.get(root) === subtotalSignature) return;

    essentialCartSubtotalSignatures.set(root, subtotalSignature);
    essentialCartRoots.add(root);
    addStyle(
      root,
      'nexara-essential-cart-totals-style',
      `[data-nexara-essential-cart-totals] {
         box-sizing: border-box;
         display: grid;
         gap: 0.8rem;
         width: 100%;
         padding: 0 1.6rem 1.6rem;
       }
       [data-nexara-essential-cart-row] {
         color: #303030 !important;
         width: 100%;
       }
       [data-nexara-essential-cart-row] [data-nexara-essential-cart-label],
       [data-nexara-essential-cart-row] [data-nexara-essential-cart-value] {
         color: #303030 !important;
       }
       [data-nexara-essential-cart-row="total"] {
         border-top: 1px solid rgba(0, 0, 0, 0.12);
         padding-top: 0.8rem;
         font-weight: 700;
       }`
    );

    if (Date.now() < essentialCartNextRetryAt || essentialCartSyncTimer) return;
    essentialCartSyncTimer = window.setTimeout(() => {
      essentialCartSyncTimer = 0;
      syncEssentialCartTotals();
    }, 100);
  }

  const setImportant = (element, property, value) => {
    if (
      element.style.getPropertyValue(property) === value &&
      element.style.getPropertyPriority(property) === 'important'
    ) return;
    element.style.setProperty(property, value, 'important');
  };

  const addStyle = (root, id, css) => {
    if (root.querySelector(`#${id}`)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = css;
    const target = root === document ? document.head : root;
    target.appendChild(style);
  };

  const scheduleRepair = () => {
    if (repairScheduled) return;
    repairScheduled = true;
    window.setTimeout(() => {
      repairScheduled = false;
      repairAccessibility();
    }, 0);
  };

  const repairShadowRoot = (root) => {
    if (!observedShadowRoots.has(root)) {
      const observer = new MutationObserver(() => {
        repairShadowRoot(root);
        scheduleRepair();
      });
      observer.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-label', 'class', 'id', 'style'],
      });
      observedShadowRoots.add(root);
    }

    const variantSelects = root.querySelectorAll(
      'select#custom-variant-selector, select[id^="nexara-recommendation-variant-"]'
    );
    const language = document.documentElement.lang.slice(0, 2);
    const shippingFee = formatConvertedAud(850);
    const freeShippingThreshold = formatConvertedAud(7000);

    repairShippingTextNodes(root, shippingFee, freeShippingThreshold);
    scheduleEssentialCartTotals(root);

    variantSelects.forEach((select) => {
      if (!select.id.startsWith('nexara-recommendation-variant-')) {
        variantIdSequence += 1;
        select.id = `nexara-recommendation-variant-${variantIdSequence}`;
      }
      if (!normaliseText(select.getAttribute('aria-label'))) {
        select.setAttribute('aria-label', labels[language] || labels.en);
      }
    });

    if (variantSelects.length) {
      addStyle(
        root,
        'nexara-a11y-recommendation-style',
        `:host,
         .section_content {
           background: #0a2540 !important;
           color: #fff !important;
         }
         [id^="nexara-recommendation-variant-"],
         label.heading_font,
         p.heading_font,
         .body_font p,
         .body_font del {
           color: #fff !important;
         }
         [id^="nexara-recommendation-variant-"] {
           background: #fff !important;
         }
         button#checkout_btn,
         button.button-container {
           background: #1d4ed8 !important;
           color: #fff !important;
         }
         button#checkout_btn span,
         button.button-container span {
           color: #fff !important;
         }`
      );

      root.querySelectorAll('label.heading_font, p.heading_font, .body_font p, .body_font del').forEach((element) => {
        setImportant(element, 'color', '#fff');
      });

      variantSelects.forEach((select) => {
        setImportant(select, 'background-color', '#fff');
        setImportant(select, 'color', '#0a2540');
      });
    }

    root.querySelectorAll('button#checkout_btn, button.button-container').forEach((button) => {
      setImportant(button, 'background-color', '#1d4ed8');
      setImportant(button, 'color', '#fff');
      button.querySelectorAll('span').forEach((span) => setImportant(span, 'color', '#fff'));
    });

    const stickyButtons = root.querySelectorAll('.essential-preorder-extra-add-to-cart-button');
    let externalStickyVisible = false;

    stickyButtons.forEach((button) => {
      button.removeAttribute('aria-label');
      externalStickyVisible = true;
    });

    if (root.host && root.host.tagName === 'SHOP-FOLLOW-BUTTON') {
      addStyle(
        root,
        'nexara-a11y-shop-follow-style',
        'button.group span.cursor-pointer { color: #fff !important; }'
      );
      root.querySelectorAll('button.group span.cursor-pointer').forEach((span) => {
        setImportant(span, 'color', '#fff');
      });
    }

    for (const element of root.querySelectorAll('*')) {
      if (element.shadowRoot) {
        externalStickyVisible = repairShadowRoot(element.shadowRoot) || externalStickyVisible;
      }
    }

    return externalStickyVisible;
  };

  const repairAccessibility = () => {
    let externalStickyVisible = false;

    repairPlugCompatibility();
    repairShippingCurrency();

    document.querySelectorAll('.section-selected-scheme[id]').forEach((element) => {
      element.removeAttribute('id');
    });

    document.querySelectorAll('#settings-template').forEach((element, index) => {
      if (index > 0) element.id = `settings-template-${index + 1}`;
    });

    document.querySelectorAll('.essential-preorder-extra-add-to-cart-button').forEach((button) => {
      button.removeAttribute('aria-label');
    });

    document.querySelectorAll(
      '.product-card-wrapper .card__heading, .product-card-wrapper .card__heading a, .product-card-wrapper .card-information, .product-card-wrapper .card-information > *, .product-card-wrapper .price, .product-card-wrapper .price-item, .product-card-wrapper .rating-count, .product-card-wrapper .rating-count span'
    ).forEach((element) => setImportant(element, 'color', '#0a2540'));

    for (const element of document.querySelectorAll('*')) {
      if (element.shadowRoot) {
        externalStickyVisible = repairShadowRoot(element.shadowRoot) || externalStickyVisible;
      }
    }

    (window.nexaraObservedShadowRoots || []).forEach((root) => {
      externalStickyVisible = repairShadowRoot(root) || externalStickyVisible;
    });

    document.documentElement.classList.toggle('nexara-external-sticky-cart-active', externalStickyVisible);
  };

  const installShadowRootObserver = () => {
    if (Element.prototype.attachShadow.nexaraObserved) return;
    const attachShadow = Element.prototype.attachShadow;
    const observedAttachShadow = function (options) {
      const root = attachShadow.call(this, options);
      repairShadowRoot(root);
      return root;
    };
    observedAttachShadow.nexaraObserved = true;
    Element.prototype.attachShadow = observedAttachShadow;
  };

  const start = () => {
    installShadowRootObserver();
    addStyle(
      document,
      'nexara-essential-cart-overlay-style',
      '.essential-cart__open shopify-chat { display: none !important; }'
    );
    repairAccessibility();
    const observer = new MutationObserver(repairAccessibility);
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-label', 'class', 'id', 'style'],
    });

    let checks = 0;
    const timer = window.setInterval(() => {
      repairAccessibility();
      checks += 1;
      if (checks >= 60) window.clearInterval(timer);
    }, 500);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
